export const joinClassNames = (...classLists) => {
  const values = classLists
    .flatMap((value) => (value ? String(value).split(' ') : []))
    .map((value) => value.trim())
    .filter(Boolean);

  return values.length > 0 ? Array.from(new Set(values)).join(' ') : undefined;
};

const EDGE_VALUE_LABELS_ENABLED = process.env.REACT_APP_GRAPH_EDGE_VALUE_LABELS !== 'false';

const parseNumericValue = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') return null;
  if (typeof rawValue === 'number') return Number.isFinite(rawValue) ? rawValue : null;

  const normalized = String(rawValue).replace(/\./g, '').replace(',', '.').trim();
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyValue = (value) => (
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
);

const getTransactionValueLabel = (edgeData) => {
  const candidates = [
    edgeData.transaction_value,
    edgeData.transactionValue,
    edgeData.value,
    edgeData.amount,
    edgeData.weight,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const numericValue = parseNumericValue(candidates[i]);
    if (numericValue !== null) {
      return formatCurrencyValue(numericValue);
    }
  }

  return null;
};

const resolveEdgeId = (edge) => {
  const preferredId = edge.operation_id
    || edge.data?.operation_id
    || edge.operationId
    || edge.data?.operationId
    || edge.transaction_id
    || edge.data?.transaction_id
    || edge.transactionId
    || edge.data?.transactionId;

  const fallbackId = edge.id || edge.data?.id;

  if (preferredId) {
    return String(preferredId);
  }

  if (fallbackId) {
    return String(fallbackId);
  }

  return `${edge.source || edge.data?.source}-${edge.target || edge.data?.target}`;
};

export const normalizeGraphPayload = ({ dataSource, currentCenterNodeId, destinyNodeId }) => {
  if (!dataSource) {
    return { nodes: [], edges: [] };
  }

  let rawNodes = null;
  let rawEdges = null;

  if (Array.isArray(dataSource)) {
    rawNodes = dataSource.filter((element) => element?.data && !element.data.source && !element.data.target);
    rawEdges = dataSource.filter((element) => element?.data && (element.data.source || element.data.target));
  } else if (Array.isArray(dataSource.elements)) {
    rawNodes = dataSource.elements.filter((element) => element?.data && !element.data.source && !element.data.target);
    rawEdges = dataSource.elements.filter((element) => element?.data && (element.data.source || element.data.target));
  } else if (Array.isArray(dataSource.nodes) && Array.isArray(dataSource.edges)) {
    rawNodes = dataSource.nodes;
    rawEdges = dataSource.edges;
  }

  if (!Array.isArray(rawNodes) || !Array.isArray(rawEdges)) {
    return { nodes: [], edges: [] };
  }

  const edges = rawEdges.map((edge) => {
    const baseEdgeData = {
      ...edge,
      ...edge.data,
      id: resolveEdgeId(edge),
      source: edge.source || edge.data?.source,
      target: edge.target || edge.data?.target,
      appointments: edge.appointments || edge.data?.appointments || [],
    };

    const transactionValueLabel = EDGE_VALUE_LABELS_ENABLED ? getTransactionValueLabel(baseEdgeData) : null;
    const edgeData = {
      ...baseEdgeData,
      label: transactionValueLabel || baseEdgeData.label || edge.label || edge.data?.label,
    };

    const classes = (edge.classes || edge.class) ? String(edge.classes || edge.class).split(' ') : [];
    if (edgeData.source === currentCenterNodeId) {
      classes.push('edge-out');
    } else if (edgeData.target === currentCenterNodeId) {
      classes.push('edge-in');
    }
    if (destinyNodeId && edgeData.source === currentCenterNodeId && edgeData.target === destinyNodeId) {
      classes.push('highlighted');
    }
    const classString = classes.length > 0 ? Array.from(new Set(classes)).join(' ') : '';
    return {
      data: edgeData,
      classes: classString,
    };
  });

  const nodesWithPendency = new Set();
  edges.forEach((edge) => {
    if (edge.data.appointments && edge.data.appointments.length > 0) {
      nodesWithPendency.add(edge.data.source);
      nodesWithPendency.add(edge.data.target);
    }
  });

  const nodes = rawNodes.map((node) => {
    const nodeId = node.id || node.data?.id;
    const classes = node.classes ? node.classes.split(' ') : [];

    if (nodeId === currentCenterNodeId || nodeId === destinyNodeId) {
      classes.push('highlighted');
    }

    if (nodesWithPendency.has(nodeId)) {
      classes.push('hasPendency');
    }

    return {
      data: {
        id: nodeId,
        label: node.label || node.data?.label || nodeId,
        ...node.data,
      },
      position: node.position,
      classes: classes.length > 0 ? Array.from(new Set(classes)).join(' ') : node.classes,
    };
  });

  return { nodes, edges };
};

export const normalizeGraphSourcesForMerge = ({
  graphData,
  transactionGraphData,
  expandedGraphData,
  previewGraphData,
  currentCenterNodeId,
  destinyNodeId,
}) => {
  const baseSources = [graphData || transactionGraphData, previewGraphData]
    .filter(Boolean)
    .map((dataSource) => normalizeGraphPayload({
      dataSource,
      currentCenterNodeId,
      destinyNodeId,
    }));

  const expandedSources = (expandedGraphData || [])
    .filter((entry) => entry?.data && entry?.nodeId)
    .map((entry) => normalizeGraphPayload({
      dataSource: entry.data,
      currentCenterNodeId: entry.nodeId,
      destinyNodeId,
    }));

  return [...baseSources, ...expandedSources];
};

export const mergeNormalizedGraphParts = (parts, currentCenterNodeId) => {
  const nodeMap = new Map();
  const edgeMap = new Map();

  parts.forEach(({ nodes, edges }) => {
    nodes.forEach((node) => {
      const nodeId = node?.data?.id;
      if (!nodeId) return;

      const existing = nodeMap.get(nodeId);
      nodeMap.set(nodeId, existing ? {
        ...existing,
        ...node,
        data: {
          ...existing.data,
          ...node.data,
        },
        position: existing.position || node.position,
        classes: joinClassNames(existing.classes, node.classes),
      } : node);
    });

    edges.forEach((edge) => {
      const edgeId = edge?.data?.id;
      if (!edgeId) return;

      const existing = edgeMap.get(edgeId);
      edgeMap.set(edgeId, existing ? {
        ...existing,
        ...edge,
        data: {
          ...existing.data,
          ...edge.data,
        },
        classes: joinClassNames(existing.classes || existing.class, edge.classes || edge.class),
      } : edge);
    });
  });

  const edgeNeighbors = new Map();
  edgeMap.forEach((edge) => {
    const sourceId = edge.data.source;
    const targetId = edge.data.target;
    if (!sourceId || !targetId) return;

    if (!edgeNeighbors.has(sourceId)) edgeNeighbors.set(sourceId, []);
    if (!edgeNeighbors.has(targetId)) edgeNeighbors.set(targetId, []);

    edgeNeighbors.get(sourceId).push(targetId);
    edgeNeighbors.get(targetId).push(sourceId);
  });

  const hopDistances = new Map();
  const queue = [[currentCenterNodeId, 0]];
  const visited = new Set();

  while (queue.length > 0) {
    const [nodeId, distance] = queue.shift();
    if (!nodeId || visited.has(nodeId)) continue;

    visited.add(nodeId);
    hopDistances.set(nodeId, distance);

    const neighbors = edgeNeighbors.get(nodeId) || [];
    neighbors.forEach((neighborId) => {
      if (!visited.has(neighborId)) {
        queue.push([neighborId, distance + 1]);
      }
    });
  }

  const nodes = Array.from(nodeMap.values()).map((node) => ({
    ...node,
    data: {
      ...node.data,
      hop: hopDistances.get(node.data.id) ?? 99,
    },
  }));

  return [...nodes, ...Array.from(edgeMap.values())];
};
